import { makeAutoObservable, observable } from "mobx";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";

import type { CustomFieldsPageQuery } from "@/queries/custom-fields";
import type {
  TCustomFieldCategory,
  TCustomFieldKind,
  TCustomFieldSortKey,
} from "@/types/custom-field";

export type { TCustomFieldSortKey } from "@/types/custom-field";

export type TSortDir = "asc" | "desc";

/**
 * The settings Global Fields table's UI state: its filters, sort and row
 * selection. The rows are the cached `customFields.page` pages
 * (`useCustomFieldsPage`, NG-2805), read under `pageQuery`; the bank every
 * other reader needs is `customFields.bank` (`useCustomFieldBank`, NG-2802).
 */
export class WorkspaceCustomFieldsStore {
  searchQuery = "";
  showArchived = false;
  kindFilter = observable.set<TCustomFieldKind>();
  /** Global Fields "Category" buckets (NG-854). Empty means every category.
   *  `system` is carried here even though the server matches no bank field on
   *  it — the list route reads this set to decide whether to inject its
   *  system rows, so dropping it would lose that signal. */
  categoryFilter = observable.set<TCustomFieldCategory>();
  sortKey: TCustomFieldSortKey = "name";
  sortDir: TSortDir = "asc";
  // Row-selection state for the management list (drives CSV-of-selection).
  rowSelection: RowSelectionState = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasActiveFilters(): boolean {
    return (
      this.searchQuery.trim().length > 0 || this.kindFilter.size > 0 || this.categoryFilter.size > 0
    );
  }

  /** The table's server query: the filters and sort the pages and the CSV
   *  export are read under. A new object only when one of them changes. */
  get pageQuery(): CustomFieldsPageQuery {
    const query: CustomFieldsPageQuery = {
      sort: this.sortKey,
      dir: this.sortDir,
      include_archived: this.showArchived,
    };
    const q = this.searchQuery.trim();
    if (q) query.q = q;
    if (this.kindFilter.size > 0) query.kind = [...this.kindFilter];
    if (this.categoryFilter.size > 0) query.category = [...this.categoryFilter];
    return query;
  }

  // ---- Filter / sort setters --------------------------------------------

  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  setShowArchived(value: boolean): void {
    this.showArchived = value;
  }

  setKindFilter(kinds: Iterable<TCustomFieldKind>): void {
    this.kindFilter.replace([...kinds]);
  }

  setCategoryFilter(categories: Iterable<TCustomFieldCategory>): void {
    this.categoryFilter.replace([...categories]);
  }

  /** Whether the given category is currently in scope. An empty filter means
   *  "every category", so it answers true for all of them — which is what lets
   *  the list route ask about its injected system rows without special-casing
   *  the unfiltered state. */
  isCategoryInScope(category: TCustomFieldCategory): boolean {
    return this.categoryFilter.size === 0 || this.categoryFilter.has(category);
  }

  /** Single-column sort as TanStack `SortingState`, derived from the store's
   *  `sortKey`/`sortDir`. The list is always sorted (defaults to name ascending),
   *  so this never returns an empty array. */
  get sorting(): SortingState {
    return [{ id: this.sortKey, desc: this.sortDir === "desc" }];
  }

  /** Bridge a TanStack `SortingState` back into `sortKey`/`sortDir`. Column ids
   *  are the server sort tokens (name / display_id / kind / updated). Clearing
   *  the sort (3rd header click) falls back to the default name-ascending so the
   *  server query always carries a valid sort. */
  setSortingFromState(next: SortingState): void {
    const sort = next[0];
    if (sort) {
      this.sortKey = sort.id as TCustomFieldSortKey;
      this.sortDir = sort.desc ? "desc" : "asc";
    } else {
      this.sortKey = "name";
      this.sortDir = "asc";
    }
  }

  clearFilters(): void {
    this.searchQuery = "";
    this.kindFilter.clear();
    this.categoryFilter.clear();
  }

  setRowSelection(next: RowSelectionState): void {
    this.rowSelection = next;
  }
}
